/**
 * Ad scheduling policy (pure logic, no React/Preact).
 *
 * Decides *whether* and *which* monetisation overlay to show, and persists the
 * cooldown / pacing state in localStorage. The hook (`useAdManager`) wires this
 * to component state and the video lifecycle.
 *
 * Overlay kinds:
 *   - 'gate'         : ask for notification permission (first run, ungranted)
 *   - 'interstitial' : native interstitial ad
 *   - 'appopen'      : "app open" style prompt (default-browser / notif request)
 */
import { getItem, setItem } from './storage.js';
import {
  canRequestNotifications,
  isAppOpenAvailable,
  isInterstitialAvailable,
  isNotificationGranted,
} from '../bridge/puerInterface.js';

export const AD_CONFIG = Object.freeze({
  firstShowDelayMs: 25_000,
  cooldownMs: 65_000,
  retryWhenNotReadyMs: 5_000,
  interstitialsBeforeAppopen: 3,
});

export const Keys = Object.freeze({
  nextOverlayAt: '_nextOverlayAt',
  notifPermRequested: '_notifPermRequested',
  appopenViaRequestDisabled: '_appopenViaRequestDisabled',
  interSinceAppopen: '_interSinceAppopen',
  firstOverlayDone: '_firstOverlayDone',
  lastAppopenTrigger: '_lastAppopenTrigger',
});

/** Schedule the next eligible overlay `ms` from now. */
export function scheduleNext(ms) {
  setItem(Keys.nextOverlayAt, String(Date.now() + ms));
}

export function getNextOverlayAt() {
  return parseInt(getItem(Keys.nextOverlayAt) || '0', 10) || 0;
}

export function isFirstOverlayDone() {
  return getItem(Keys.firstOverlayDone) === '1';
}

export function markFirstOverlayDone() {
  setItem(Keys.firstOverlayDone, '1');
}

export function getInterstitialsSinceAppopen() {
  return parseInt(getItem(Keys.interSinceAppopen) || '0', 10) || 0;
}

export function bumpInterstitialsSinceAppopen() {
  setItem(Keys.interSinceAppopen, String(getInterstitialsSinceAppopen() + 1));
}

export function resetInterstitialsSinceAppopen() {
  setItem(Keys.interSinceAppopen, '0');
}

/**
 * Decide which overlay (if any) should be shown right now.
 * @returns {'gate'|'interstitial'|'appopen'|null}
 */
export function decideOverlay() {
  const canNotif = canRequestNotifications();
  const granted = isNotificationGranted();
  const notifRequested = getItem(Keys.notifPermRequested) === '1';

  // If the OS already granted notifications without us asking, the appopen
  // "request" variant is pointless - disable it for good.
  if (granted && !notifRequested) {
    setItem(Keys.appopenViaRequestDisabled, '1');
  }

  const firstDone = isFirstOverlayDone();
  const appOpenAvail = canNotif && isAppOpenAvailable();
  const interAvail = isInterstitialAvailable();

  if (!firstDone) return interAvail ? 'interstitial' : null;
  if (!granted && !notifRequested && canNotif) return 'gate';

  const interSince = getInterstitialsSinceAppopen();
  if (appOpenAvail && interSince >= AD_CONFIG.interstitialsBeforeAppopen) {
    return 'appopen';
  }
  return interAvail ? 'interstitial' : appOpenAvail ? 'appopen' : null;
}

/**
 * Pick the appopen sub-flow: notification request vs. default-browser request.
 * Alternates so we don't spam the same OS dialog twice in a row.
 * @returns {'browser'|'notif'}
 */
export function nextAppopenTrigger() {
  const disabled = getItem(Keys.appopenViaRequestDisabled) === '1';
  const last = getItem(Keys.lastAppopenTrigger) || '';
  return disabled || last === 'notif' ? 'browser' : 'notif';
}

export function rememberAppopenTrigger(kind) {
  setItem(Keys.lastAppopenTrigger, kind);
}
